// ProfileChangeRequestsForm.js — admin reviews pending provider
// profile change requests and approves/rejects each one.
import React, { useState, useEffect, useCallback } from "react";
import { API_IP } from "../../env";
import axios from "axios";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import ListGroup from "react-bootstrap/ListGroup";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Form from "react-bootstrap/Form";

const FIELDS = [
  { key: "title", label: "Title" },
  { key: "slogan", label: "Slogan" },
  { key: "self_description", label: "Profile" },
  { key: "credentials", label: "Credentials" },
];

const parseCredentialList = (text) =>
  (text || "")
    .split(/\n|\(\.\)/)
    .map((s) => s.trim())
    .filter(Boolean);

const formatHour = (h) => {
  if (h === null || h === undefined || h === "") return null;
  const n = Number(h);
  if (!Number.isInteger(n) || n < 0 || n > 23) return null;
  const period = n < 12 ? "AM" : "PM";
  const hour12 = ((n + 11) % 12) + 1;
  return `${hour12}:00 ${period}`;
};

const formatHourRange = (open, close) => {
  const o = formatHour(open);
  const c = formatHour(close);
  if (!o && !c) return null;
  return `${o || "—"} → ${c || "—"}`;
};

function ProfileChangeRequestsForm({ show, onHide, setRefreshProvider, refreshProvider }) {
  const [requestList, setRequestList] = useState([]);
  const [notes, setNotes] = useState({});

  async function axiosReqConfig(url, method, data) {
    const bearerToken = localStorage.getItem("@uphealth_login_token");
    return await axios({
      url,
      method,
      headers: { Authorization: `Bearer ${bearerToken}` },
      data,
    });
  }

  const fetchRequests = useCallback(async () => {
    try {
      const { data } = await axiosReqConfig(
        `${API_IP}/api/admin/providers/profile-change-requests`,
        "get"
      );
      setRequestList(data || []);
    } catch (err) {
      console.error("Failed to fetch profile change requests", err);
    }
  }, []);

  useEffect(() => {
    if (show) fetchRequests();
  }, [show, fetchRequests]);

  async function reviewRequest(requestId, decision) {
    const verb = decision === "approve" ? "approve" : "reject";
    if (!window.confirm(`Are you sure you want to ${verb} this request?`)) return;
    try {
      await axiosReqConfig(
        `${API_IP}/api/admin/providers/profile-change-requests/${requestId}`,
        "patch",
        { decision, admin_note: notes[requestId] || null }
      );
      setNotes((prev) => {
        const next = { ...prev };
        delete next[requestId];
        return next;
      });
      await fetchRequests();
      if (setRefreshProvider) setRefreshProvider(!refreshProvider);
    } catch (err) {
      window.alert("Could not submit review.");
    }
  }

  function renderHoursDiff(request) {
    const reqOpen = request.requested_opening_hour;
    const reqClose = request.requested_closing_hour;
    const hasRequest =
      (reqOpen !== null && reqOpen !== undefined) ||
      (reqClose !== null && reqClose !== undefined);
    if (!hasRequest) return null;

    const currentRange =
      formatHourRange(
        request.current_opening_hour,
        request.current_closing_hour
      ) || "(not set)";
    const requestedRange =
      formatHourRange(reqOpen, reqClose) || "(not set)";

    return (
      <div
        style={{
          borderTop: "1px solid #eee",
          paddingTop: 8,
          marginTop: 8,
        }}
      >
        <div className="fw-bold small text-uppercase text-muted">
          Operating Hours
        </div>
        <Row>
          <Col xs={6}>
            <div className="small text-muted">Current</div>
            <div className="small">{currentRange}</div>
          </Col>
          <Col xs={6}>
            <div className="small text-muted">Requested</div>
            <div
              className="small"
              style={{
                backgroundColor: "#fff8e1",
                padding: 4,
                borderRadius: 4,
                fontWeight: 600,
              }}
            >
              {requestedRange}
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  function renderDiff(request) {
    return (
      <div className="mt-2">
        {FIELDS.map((field) => {
          const requested = request[`requested_${field.key}`];
          if (!requested) return null;
          const current = request[`current_${field.key}`];
          const isList = field.key === "credentials";
          const renderValue = (value, highlight) => {
            if (isList) {
              const items = parseCredentialList(value);
              if (items.length === 0) return <em>(empty)</em>;
              return (
                <ul
                  className="small mb-0"
                  style={{
                    paddingLeft: 18,
                    backgroundColor: highlight ? "#fff8e1" : undefined,
                    borderRadius: highlight ? 4 : undefined,
                    padding: highlight ? "6px 6px 6px 24px" : undefined,
                  }}
                >
                  {items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              );
            }
            return (
              <div
                className="small"
                style={{
                  whiteSpace: "pre-wrap",
                  backgroundColor: highlight ? "#fff8e1" : undefined,
                  padding: highlight ? 4 : undefined,
                  borderRadius: highlight ? 4 : undefined,
                }}
              >
                {value || <em>(empty)</em>}
              </div>
            );
          };

          return (
            <div
              key={field.key}
              style={{
                borderTop: "1px solid #eee",
                paddingTop: 8,
                marginTop: 8,
              }}
            >
              <div className="fw-bold small text-uppercase text-muted">
                {field.label}
              </div>
              <Row>
                <Col xs={6}>
                  <div className="small text-muted">Current</div>
                  {renderValue(current, false)}
                </Col>
                <Col xs={6}>
                  <div className="small text-muted">Requested</div>
                  {renderValue(requested, true)}
                </Col>
              </Row>
            </div>
          );
        })}
        {renderHoursDiff(request)}
      </div>
    );
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Profile Change Requests</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ListGroup variant="flush">
          {requestList.length > 0 ? (
            requestList.map((request) => (
              <ListGroup.Item key={request.request_id} className="py-3">
                <Row className="align-items-start">
                  <Col>
                    <div className="fw-bold">{request.provider_name}</div>
                    <div className="small text-muted">
                      {request.email} · submitted{" "}
                      {new Date(request.submitted_at).toLocaleString()}
                    </div>
                    {renderDiff(request)}
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Optional note for the provider (e.g. reason for rejection)"
                      className="mt-3"
                      value={notes[request.request_id] || ""}
                      onChange={(e) =>
                        setNotes((prev) => ({
                          ...prev,
                          [request.request_id]: e.target.value,
                        }))
                      }
                    />
                  </Col>
                  <Col xs="auto">
                    <ButtonGroup vertical>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() =>
                          reviewRequest(request.request_id, "approve")
                        }
                      >
                        Approve
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        className="mt-2"
                        onClick={() =>
                          reviewRequest(request.request_id, "reject")
                        }
                      >
                        Reject
                      </Button>
                    </ButtonGroup>
                  </Col>
                </Row>
              </ListGroup.Item>
            ))
          ) : (
            <p className="text-muted mb-0">
              No pending profile change requests.
            </p>
          )}
        </ListGroup>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ProfileChangeRequestsForm;
